/**
 * Ticket view constants and date-range helpers
 *
 * Shared by the ticket fetching and search hooks (useTicketData,
 * useTicketSearch) and re-exported from useAdminReports for screens.
 */

// Ticket view presets - combines status and assignment filters
// Note: "Unassigned" merged into "All Open" (default behavior with toggle)
//
// `labelKey`/`shortLabelKey` are resolved by the rendering screen so the chips
// re-translate on a language change; `label`/`shortLabel` stay as the English
// fallback for any consumer that is not inside a React render.
export const TICKET_VIEW_OPTIONS = [
  {
    key: 'myActive',
    label: 'My Active',
    shortLabel: 'My Active',
    labelKey: 'admin:filterHome.views.myActive',
    shortLabelKey: 'admin:filterHome.views.myActiveShort',
  },
  {
    key: 'myClosed',
    label: 'My Closed',
    shortLabel: 'My Closed',
    labelKey: 'admin:filterHome.views.myClosed',
    shortLabelKey: 'admin:filterHome.views.myClosedShort',
  },
  {
    key: 'allOpen',
    label: 'All Open',
    shortLabel: 'Open',
    labelKey: 'admin:filterHome.views.allOpen',
    shortLabelKey: 'admin:filterHome.views.allOpenShort',
  },
  {
    key: 'allInProgress',
    label: 'All In Progress',
    shortLabel: 'In Progress',
    labelKey: 'admin:filterHome.views.allInProgress',
    shortLabelKey: 'admin:filterHome.views.allInProgressShort',
  },
  {
    key: 'allClosed',
    label: 'All Closed',
    shortLabel: 'Closed',
    labelKey: 'admin:filterHome.views.allClosed',
    shortLabelKey: 'admin:filterHome.views.allClosedShort',
  },
];

// Views that have time constraints by default (past 30 days)
export const TIME_CONSTRAINED_VIEWS = ['myClosed', 'allOpen', 'allInProgress', 'allClosed'];

// Standard 8-4-4-4-12 hex UUID. Matches when the user pastes a full report ID
// into the search bar — we then bypass embedding search and fetch directly.
export const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Default time constraint: 30 days in milliseconds
export const DEFAULT_TIME_CONSTRAINT_MS = 30 * 24 * 60 * 60 * 1000;

// Page size for paginated requests (stay safely under 6MB API Gateway limit)
export const PAGE_SIZE = 100;

/**
 * Convert a 'YYYY-MM-DD' date string to local-time epoch millis.
 * Start of day by default, end of day when endOfDay is true. Null when
 * empty/invalid so partial input never produces a NaN bound.
 */
export const dateStringToMillis = (dateStr, endOfDay = false) => {
  if (!dateStr) return null;
  const ms = new Date(dateStr + (endOfDay ? 'T23:59:59.999' : 'T00:00:00')).getTime();
  return Number.isNaN(ms) ? null : ms;
};

/**
 * Client-side date-range filter (used for search results, which don't go
 * through the server-side timestamp-bounded ticket queries)
 */
export const filterByDateRange = (items, startStr, endStr) => {
  const min = dateStringToMillis(startStr, false);
  const max = dateStringToMillis(endStr, true);
  if (min == null && max == null) return items;
  return items.filter((r) => (min == null || r.timestamp >= min) && (max == null || r.timestamp <= max));
};
